import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import axios from 'axios';
import { ArrowRight, Check } from 'lucide-react';

/**
 * Enquiry form shared by the public landing pages.
 *
 * Posts to /api/public/enquiries (WebsiteEnquiryController): the row is stored first, then the
 * shared inbox is notified. The address stays visible under the form as the fallback for anyone who
 * would rather write directly, and the form never depends on a mail handler on the visitor's machine.
 */
export const CONTACT_EMAIL = 'info@axiomflowai.com';
export const mailtoHref = (subject: string) => `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;
export const ENQUIRY_ENDPOINT = '/api/public/enquiries';

const MINT = '#54e6c6';
const EASE = [0.22, 1, 0.36, 1] as const;

const Backdrop = styled(motion.div)`
  position: fixed; inset: 0; z-index: 1000; display: grid; place-items: center; padding: 20px;
  background: rgba(8, 8, 10, 0.74); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
  overflow-y: auto;
`;
const Card = styled(motion.div)`
  width: min(560px, 100%); border-radius: 20px; padding: clamp(26px, 4vw, 38px);
  background: #101013; color: #f4efe7; border: 1px solid rgba(244, 239, 231, 0.14);
  box-shadow: 0 40px 120px rgba(0, 0, 0, 0.55); font-family: 'Inter', sans-serif;
  display: grid; gap: 18px; margin: auto 0;
`;
const Label = styled.div` font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.16em; text-transform: uppercase; color: ${MINT}; `;
const Title = styled.h3` margin: 0; font-family: 'Fraunces', serif; font-weight: 400; font-size: clamp(1.5rem, 3vw, 2rem); line-height: 1.15; letter-spacing: -0.015em; `;
const Lede = styled.p` margin: 0; font-size: 15px; line-height: 1.6; opacity: 0.72; max-width: 48ch; `;
const Form = styled.form` display: grid; gap: 14px; `;
const Two = styled.div`
  display: grid; gap: 14px; grid-template-columns: 1fr 1fr;
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;
const Field = styled.label`
  display: grid; gap: 6px;
  span { font-size: 12px; letter-spacing: 0.04em; opacity: 0.7; }
  span em { font-style: normal; color: ${MINT}; }
`;
const inputStyle = `
  width: 100%; box-sizing: border-box; min-height: 44px; padding: 10px 14px; border-radius: 10px;
  border: 1px solid rgba(244, 239, 231, 0.18); background: rgba(244, 239, 231, 0.04); color: #f4efe7;
  font: 400 15px 'Inter', sans-serif; outline: none; transition: border-color .2s, box-shadow .2s;
  &::placeholder { color: rgba(244, 239, 231, 0.32); }
  &:focus { border-color: ${MINT}; box-shadow: 0 0 0 3px ${MINT}2e; }
  &[aria-invalid='true'] { border-color: #f08f7a; }
`;
const Input = styled.input` ${inputStyle} `;
const TextArea = styled.textarea` ${inputStyle} min-height: 112px; resize: vertical; line-height: 1.5; `;
/* Off-screen, never focusable: a bot that fills every field fills this one. */
const Honeypot = styled.div` position: absolute; left: -10000px; top: auto; width: 1px; height: 1px; overflow: hidden; `;
const Row = styled.div` display: flex; align-items: center; gap: 12px; flex-wrap: wrap; `;
const Submit = styled.button`
  display: inline-flex; align-items: center; gap: 9px; min-height: 44px; padding: 0 22px; border-radius: 999px; border: 0; cursor: pointer;
  background: ${MINT}; color: #04120e; font: 700 14px 'Inter', sans-serif; transition: transform .2s ease, box-shadow .3s ease, opacity .2s;
  &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 16px 44px ${MINT}44; }
  &:disabled { opacity: 0.6; cursor: default; }
`;
const Ghost = styled.button`
  display: inline-flex; align-items: center; min-height: 44px; padding: 0 20px; border-radius: 999px; cursor: pointer;
  border: 1px solid rgba(244,239,231,0.26); background: transparent; color: inherit; opacity: 0.82; font: 600 14px 'Inter', sans-serif;
  &:hover { opacity: 1; border-color: currentColor; }
`;
const Fine = styled.div`
  font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 0.08em; opacity: 0.5; line-height: 1.7;
  a { color: inherit; text-decoration: underline; text-underline-offset: 3px; }
`;
const Problem = styled.div` font-size: 13px; line-height: 1.5; color: #f6b3a3; `;
const Done = styled.div`
  display: grid; gap: 10px; padding: 18px; border-radius: 12px; border: 1px solid ${MINT}55; background: ${MINT}12;
  strong { display: flex; align-items: center; gap: 8px; font-weight: 600; font-size: 16px; }
  p { margin: 0; font-size: 14px; line-height: 1.6; opacity: 0.8; }
`;

type Fields = { name: string; email: string; firm: string; jobTitle: string; message: string; website: string };
const EMPTY: Fields = { name: '', email: '', firm: '', jobTitle: '', message: '', website: '' };
const EMAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const ContactCard: React.FC<{
  open: boolean;
  onClose: () => void;
  /** Which call to action opened the form; stored with the enquiry as its topic. */
  subject: string;
  title: React.ReactNode;
  lede: React.ReactNode;
}> = ({ open, onClose, subject, title, lede }) => {
  const reduced = !!useReducedMotion();
  const [fields, setFields] = useState<Fields>(EMPTY);
  const [touched, setTouched] = useState<Partial<Record<keyof Fields, boolean>>>({});
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [failure, setFailure] = useState<string>('');
  const nameRef = useRef<HTMLInputElement>(null);
  const href = mailtoHref(subject);

  useEffect(() => {
    if (!open) return;
    setState('idle'); setFailure(''); setTouched({});
    const t = window.setTimeout(() => nameRef.current?.focus(), 60);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { window.clearTimeout(t); window.removeEventListener('keydown', onKey, true); document.body.style.overflow = prev; };
  }, [open, onClose]);

  const set = (k: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setFields(f => ({ ...f, [k]: e.target.value }));
  const blur = (k: keyof Fields) => () => setTouched(t => ({ ...t, [k]: true }));

  const errors: Partial<Record<keyof Fields, string>> = {};
  if (fields.name.trim().length < 2) errors.name = 'Your name, please.';
  if (!EMAIL_OK.test(fields.email.trim())) errors.email = 'A work e-mail we can reply to.';
  if (fields.message.trim().length < 2) errors.message = 'A line on what you run is enough.';
  const invalid = Object.keys(errors).length > 0;

  const submit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ name: true, email: true, message: true });
    if (invalid || state === 'sending') return;
    setState('sending'); setFailure('');
    try {
      await axios.post(ENQUIRY_ENDPOINT, {
        name: fields.name.trim(), email: fields.email.trim(), firm: fields.firm.trim() || null,
        jobTitle: fields.jobTitle.trim() || null, message: fields.message.trim(),
        topic: subject, page: window.location.pathname, website: fields.website,
      }, { timeout: 15000 });
      setState('sent');
      setFields(EMPTY);
    } catch (err: any) {
      const reason = err?.response?.data?.error;
      setFailure(typeof reason === 'string' ? reason : 'We could not send that just now.');
      setState('failed');
    }
  }, [fields, invalid, state, subject]);

  return (
    <AnimatePresence>
      {open && (
        <Backdrop data-contact-card initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: reduced ? 0 : 0.25 }}
          onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
          <Card role="dialog" aria-modal="true" aria-labelledby="contact-card-title"
            initial={reduced ? false : { opacity: 0, y: 14, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: reduced ? 0 : 0.35, ease: EASE }}>
            <Label>Talk to us</Label>
            <Title id="contact-card-title">{title}</Title>
            {state === 'sent' ? (
              <>
                <Done role="status">
                  <strong><Check size={18} color={MINT} /> Received. Thank you.</strong>
                  <p>We read every enquiry ourselves and reply from {CONTACT_EMAIL}, usually within a business day.</p>
                </Done>
                <Row><Ghost type="button" onClick={onClose}>Close</Ghost></Row>
              </>
            ) : (
              <>
                <Lede>{lede}</Lede>
                <Form onSubmit={submit} noValidate>
                  <Two>
                    <Field>
                      <span>Name <em>*</em></span>
                      <Input ref={nameRef} name="name" autoComplete="name" value={fields.name} onChange={set('name')} onBlur={blur('name')}
                        aria-invalid={!!(touched.name && errors.name)} aria-describedby={touched.name && errors.name ? 'enq-name-err' : undefined} />
                      {touched.name && errors.name && <Problem id="enq-name-err">{errors.name}</Problem>}
                    </Field>
                    <Field>
                      <span>Work e-mail <em>*</em></span>
                      <Input name="email" type="email" autoComplete="email" inputMode="email" value={fields.email} onChange={set('email')} onBlur={blur('email')}
                        aria-invalid={!!(touched.email && errors.email)} aria-describedby={touched.email && errors.email ? 'enq-email-err' : undefined} />
                      {touched.email && errors.email && <Problem id="enq-email-err">{errors.email}</Problem>}
                    </Field>
                  </Two>
                  <Two>
                    <Field>
                      <span>Firm</span>
                      <Input name="firm" autoComplete="organization" value={fields.firm} onChange={set('firm')} />
                    </Field>
                    <Field>
                      <span>Role</span>
                      <Input name="jobTitle" autoComplete="organization-title" value={fields.jobTitle} onChange={set('jobTitle')} />
                    </Field>
                  </Two>
                  <Field>
                    <span>What you run, and on what <em>*</em></span>
                    <TextArea name="message" value={fields.message} onChange={set('message')} onBlur={blur('message')}
                      placeholder="e.g. private credit and ABF across two funds; the borrowing-base tie-out still lives in a spreadsheet."
                      aria-invalid={!!(touched.message && errors.message)} aria-describedby={touched.message && errors.message ? 'enq-message-err' : undefined} />
                    {touched.message && errors.message && <Problem id="enq-message-err">{errors.message}</Problem>}
                  </Field>
                  <Honeypot aria-hidden="true">
                    <label>Website<input name="website" tabIndex={-1} autoComplete="off" value={fields.website} onChange={set('website')} /></label>
                  </Honeypot>
                  {state === 'failed' && (
                    <Problem role="alert">{failure} You can also write to <a href={href} style={{ color: 'inherit' }}>{CONTACT_EMAIL}</a>.</Problem>
                  )}
                  <Row>
                    <Submit type="submit" disabled={state === 'sending'}>
                      {state === 'sending' ? 'Sending…' : <>Send <ArrowRight size={15} /></>}
                    </Submit>
                    <Ghost type="button" onClick={onClose}>Close</Ghost>
                  </Row>
                </Form>
                <Fine>Goes to the founder, not a queue. Prefer e-mail? <a href={href}>{CONTACT_EMAIL}</a>. We use what you send only to reply.</Fine>
              </>
            )}
          </Card>
        </Backdrop>
      )}
    </AnimatePresence>
  );
};

export default ContactCard;
